import React, { useState } from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import {
  DownOutlined,
  UpOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import RefreshIcon from 'src/common/icons/RefreshIcon';
import { formatDateTime, transformName } from 'src/utils/utils';

interface EventTypeConfig {
  statusBg: string;
  statusColor: string;
  statusText: string;
  borderColor: string;
  icon: React.ComponentType<any> | null;
  iconColor: string;
  descriptionPrefix: string | null;
  descriptionAction: string;
  descriptionActionColor: string;
  hasDetailsTable: boolean;
  isRejectionType: boolean;
  isDelete: boolean;
  isCreate: boolean;
  supportsMultipleCharges: boolean;
}

interface EventsProps {
  events?: any[];
  classes?: any;
  loading?: boolean;
  currencySymbol?: string;
}

interface ChargeData {
  chargeId: string;
  previousValue?: number;
  currentValue?: number;
  previousAttachment?: string;
  currentAttachment?: string;
  previousRemarks?: string;
  currentRemarks?: string;
}

const DEFAULT_CONFIG: EventTypeConfig = {
  statusBg: '#F9F0FF',
  statusColor: '#722ED1',
  statusText: 'Charge Updated',
  borderColor: '#722ED1',
  icon: RefreshIcon,
  iconColor: '#1659CB',
  descriptionPrefix: null,
  descriptionAction: 'Updated',
  descriptionActionColor: '#1659CB',
  hasDetailsTable: true,
  isRejectionType: false,
  isDelete: false,
  isCreate: false,
  supportsMultipleCharges: true,
};

const EVENT_TYPE_CONFIG: Record<string, EventTypeConfig> = {
  rejected: {
    statusBg: '#FBE7E5',
    statusColor: '#D40B00',
    statusText: 'Rejected',
    borderColor: '#D40B00',
    icon: null,
    iconColor: '#D40B00',
    descriptionPrefix: null,
    descriptionAction: '',
    descriptionActionColor: '#D40B00',
    hasDetailsTable: false,
    isRejectionType: true,
    isDelete: false,
    isCreate: false,
    supportsMultipleCharges: false,
  },
  reject_payout: {
    statusBg: '#FBE7E5',
    statusColor: '#D40B00',
    statusText: 'Rejected',
    borderColor: '#D40B00',
    icon: null,
    iconColor: '#D40B00',
    descriptionPrefix: null,
    descriptionAction: '',
    descriptionActionColor: '#D40B00',
    hasDetailsTable: false,
    isRejectionType: true,
    isDelete: false,
    isCreate: false,
    supportsMultipleCharges: false,
  },
  review_pending: {
    statusBg: '#FFF5EB',
    statusColor: '#E67905',
    statusText: 'Review Pending',
    borderColor: '#E67905',
    icon: ClockCircleOutlined,
    iconColor: '#E67905',
    descriptionPrefix: 'Payout',
    descriptionAction: 'Review Pending',
    descriptionActionColor: '#E67905',
    hasDetailsTable: false,
    isRejectionType: false,
    isDelete: false,
    isCreate: false,
    supportsMultipleCharges: false,
  },
  approve_payout: {
    statusBg: 'rgba(20, 138, 39, 0.1)',
    statusColor: '#148A27',
    statusText: 'Approved',
    borderColor: '#148A27',
    icon: CheckOutlined,
    iconColor: '#148A27',
    descriptionPrefix: 'Payout',
    descriptionAction: 'Approved',
    descriptionActionColor: '#148A27',
    hasDetailsTable: false,
    isRejectionType: false,
    isDelete: false,
    isCreate: false,
    supportsMultipleCharges: false,
  },
  deleted: {
    statusBg: 'rgba(212,11,0,0.1)',
    statusColor: '#D40B00',
    statusText: 'Charge Deleted',
    borderColor: '#D40B00',
    icon: DeleteOutlined,
    iconColor: '#D40B00',
    descriptionPrefix: null,
    descriptionAction: 'deleted',
    descriptionActionColor: '#D40B00',
    hasDetailsTable: true,
    isRejectionType: false,
    isDelete: true,
    isCreate: false,
    supportsMultipleCharges: false,
  },
  updated: {
    statusBg: '#F9F0FF',
    statusColor: '#722ED1',
    statusText: 'Charge Updated',
    borderColor: '#722ED1',
    icon: RefreshIcon,
    iconColor: '#1659CB',
    descriptionPrefix: null,
    descriptionAction: 'Updated',
    descriptionActionColor: '#1659CB',
    hasDetailsTable: true,
    isRejectionType: false,
    isDelete: false,
    isCreate: false,
    supportsMultipleCharges: true,
  },
  adhoc_created: {
    statusBg: 'rgba(0,153,255,0.1)',
    statusColor: '#0099FF',
    statusText: 'Charge Added',
    borderColor: '#0099FF',
    icon: PlusOutlined,
    iconColor: '#0099FF',
    descriptionPrefix: null,
    descriptionAction: 'Added',
    descriptionActionColor: '#1659CB',
    hasDetailsTable: true,
    isRejectionType: false,
    isDelete: false,
    isCreate: true,
    supportsMultipleCharges: false,
  },
  created: {
    statusBg: 'rgba(0,153,255,0.1)',
    statusColor: '#0099FF',
    statusText: 'Charge Added',
    borderColor: '#0099FF',
    icon: PlusOutlined,
    iconColor: '#0099FF',
    descriptionPrefix: null,
    descriptionAction: 'Added',
    descriptionActionColor: '#1659CB',
    hasDetailsTable: true,
    isRejectionType: false,
    isDelete: false,
    isCreate: true,
    supportsMultipleCharges: false,
  },
};

const getEventConfig = (eventType: string): EventTypeConfig =>
  EVENT_TYPE_CONFIG[eventType?.toLowerCase()] || DEFAULT_CONFIG;

const formatCreatorName = (creatorName: string): string => {
  if (!creatorName) return 'User';
  if (!creatorName.includes(',')) return creatorName;
  const [name, code] = creatorName.split(',').map((part) => part.trim());
  return code ? `${name} (${code})` : name;
};

const getChargesFromEvent = (event: any): ChargeData[] => {
  const previousPayout = event.previous_data?.payout || [];
  const currentPayout = event.current_data?.payout || [];

  const allChargeIds = new Set([
    ...previousPayout.map((p: any) => p.chargeId || p.charge_id),
    ...currentPayout.map((c: any) => c.chargeId || c.charge_id),
  ]);

  return Array.from(allChargeIds).map((chargeId) => {
    const prevItem = previousPayout.find((p: any) => (p.chargeId || p.charge_id) === chargeId);
    const currItem = currentPayout.find((c: any) => (c.chargeId || c.charge_id) === chargeId);

    return {
      chargeId: chargeId as string,
      previousValue: prevItem?.payout,
      currentValue: currItem?.payout,
      previousAttachment: prevItem?.attachment,
      currentAttachment: currItem?.attachment,
      previousRemarks: prevItem?.remark,
      currentRemarks: currItem?.remark,
    };
  });
};

const styles = {
  eventsContainer: {
    backgroundColor: '#F5F4F2',
    padding: '24px',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  eventsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'relative',
    paddingLeft: '48px',
    paddingBottom: '40px',
  },
  timelineLine: {
    position: 'absolute',
    left: '30px',
    top: '0px',
    bottom: '0px',
    width: '0px',
    borderLeft: '1px dashed #0F62EE',
  },
  timelineDot: {
    position: 'absolute',
    left: '-22px',
    top: '16px',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#0F62EE',
    zIndex: 2,
    boxShadow: '0 0 15px rgba(15, 98, 238, 0.1)',
  },
  eventCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '0 8px 8px 0',
    padding: '12px 16px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  eventHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  eventLeft: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flex: 1,
  },
  eventStatus: {
    padding: '6px 10px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: '16px',
    minWidth: '164px',
    textAlign: 'center',
    fontFamily: "'Open Sans', sans-serif",
  },
  eventDescription: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.6)',
    fontFamily: "'Open Sans', sans-serif",
  },
  eventFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '6px',
  },
  eventMeta: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: "'Open Sans', sans-serif",
    paddingTop: '5px',
  },
  metaLabel: {
    color: 'rgba(0,0,0,0.8)',
  },
  metaValue: {
    color: '#333333',
  },
  metaDivider: {
    width: '1px',
    height: '16.5px',
    backgroundColor: '#DDDDDD',
  },
  metaTime: {
    color: 'rgba(0,0,0,0.6)',
  },
  viewDetailsButton: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '14px 0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1659CB',
    fontFamily: "'Open Sans', sans-serif",
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  detailsTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#F4FBFF',
    borderBottom: '1px solid rgba(0,0,0,0.2)',
  },
  tableHeaderCell: {
    padding: '16px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'rgba(0,0,0,0.6)',
    fontFamily: "'Open Sans', sans-serif",
  },
  tableRow: {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E0E0E0',
  },
  tableCell: {
    padding: '16px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.6)',
    fontFamily: "'Open Sans', sans-serif",
  },
  viewLink: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#1659CB',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: "'Open Sans', sans-serif",
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  rejectionReason: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  rejectionReasonLabel: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: 'rgba(0,0,0,0.6)',
    fontFamily: "'Open Sans', sans-serif",
  },
  rejectionReasonText: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.6)',
    fontFamily: "'Open Sans', sans-serif",
  },
};

interface AttachmentCellProps {
  attachment?: string;
  classes: any;
}

const AttachmentCell: React.FC<AttachmentCellProps> = ({ attachment, classes }) => {
  if (!attachment) return <>-</>;

  return (
    <div
      className={classes.viewLink}
      onClick={() => window.open(attachment, '_blank')}
    >
      <EyeOutlined style={{ fontSize: 16, color: '#1659CB' }} />
      <span>View</span>
    </div>
  );
};

interface DetailsTableProps {
  event: any;
  config: EventTypeConfig;
  currencySymbol: string;
  classes: any;
}

const DetailsTable: React.FC<DetailsTableProps> = ({
  event,
  config,
  currencySymbol,
  classes,
}) => {
  if (!event.previous_data && !event.current_data) return null;

  const charges = getChargesFromEvent(event);
  const { isDelete, isCreate } = config;

  const columns = [
    { key: 'chargeId', header: 'Charge ID', show: true },
    { key: 'previousValue', header: 'Previous Value', show: !isCreate },
    { key: 'previousAttachment', header: 'Previous Attachment', show: !isCreate },
    { key: 'previousRemarks', header: 'Previous Remarks', show: !isCreate },
    { key: 'currentValue', header: 'Updated Value', show: !isDelete },
    { key: 'currentAttachment', header: 'Updated Attachment', show: !isDelete },
    { key: 'currentRemarks', header: 'Updated Remarks', show: !isDelete },
  ].filter((col) => col.show);

  const renderCell = (charge: ChargeData, columnKey: string) => {
    switch (columnKey) {
      case 'chargeId':
        return charge.chargeId;
      case 'previousValue':
        return `${currencySymbol}${charge.previousValue || ''}`;
      case 'previousAttachment':
        return <AttachmentCell attachment={charge.previousAttachment} classes={classes} />;
      case 'previousRemarks':
        return charge.previousRemarks || '-';
      case 'currentValue':
        return `${currencySymbol}${charge.currentValue || '-'}`;
      case 'currentAttachment':
        return <AttachmentCell attachment={charge.currentAttachment} classes={classes} />;
      case 'currentRemarks':
        return charge.currentRemarks || '-';
      default:
        return '-';
    }
  };

  return (
    <table className={classes.detailsTable}>
      <thead>
        <tr className={classes.tableHeader}>
          {columns.map((col) => (
            <th key={col.key} className={classes.tableHeaderCell}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {charges.map((charge) => (
          <tr key={charge.chargeId} className={classes.tableRow}>
            {columns.map((col) => (
              <td key={col.key} className={classes.tableCell}>
                {renderCell(charge, col.key)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface EventDescriptionProps {
  event: any;
  config: EventTypeConfig;
}

const EventDescription: React.FC<EventDescriptionProps> = ({ event, config }) => {
  const {
    icon: Icon,
    iconColor,
    descriptionPrefix,
    descriptionAction,
    descriptionActionColor,
    supportsMultipleCharges,
  } = config;

  const chargeCount = event.current_data?.payout?.length || 1;
  const chargeName = transformName(
    event.current_data?.payout?.[0]?.chargeId ||
    event.previous_data?.payout?.[0]?.chargeId ||
    'Charge',
  );

  // Determine the prefix text
  let prefixText = descriptionPrefix;
  if (prefixText === null) {
    prefixText = supportsMultipleCharges && chargeCount > 1
      ? `${chargeCount} Charges`
      : chargeName;
  }

  return (
    <>
      {Icon && <Icon style={{ fontSize: 12, color: iconColor }} />}
      <span>
        <span style={{ color: '#666' }}>{prefixText}</span>
        <span style={{ color: descriptionActionColor }}> {descriptionAction}</span>
      </span>
    </>
  );
};

interface EventMetaInfoProps {
  eventTime: string;
  creatorName: string;
  classes: any;
}

const EventMetaInfo: React.FC<EventMetaInfoProps> = ({ eventTime, creatorName, classes }) => (
  <div className={classes.eventMeta}>
    <span className={classes.metaTime}>
      {formatDateTime(eventTime, 'DD MMM YYYY, hh:mm A', '-')}
    </span>
    <div className={classes.metaDivider} />
    <span className={classes.metaTime}>
      By : {formatCreatorName(creatorName)}
    </span>
  </div>
);

interface EventCardProps {
  event: any;
  index: number;
  isExpanded: boolean;
  onToggle: (eventId: string) => void;
  currencySymbol: string;
  classes: any;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  index,
  isExpanded,
  onToggle,
  currencySymbol,
  classes,
}) => {
  const eventId = `${event.event_type}_${index}`;
  const config = getEventConfig(event.event_type);
  const {
    statusBg,
    statusColor,
    statusText,
    borderColor,
    hasDetailsTable,
    isRejectionType,
  } = config;
  const hasPayout = event.previous_data?.payout || event.current_data?.payout;
  const canShowDetails = hasDetailsTable && hasPayout;

  return (
    <div style={{ position: 'relative' }}>
      <div className={classes.timelineDot} />
      <div
        className={classes.eventCard}
        style={{ borderLeft: `2px solid ${borderColor}` }}
      >
        <div className={classes.eventHeader}>
          <div className={classes.eventLeft}>
            <div
              className={classes.eventStatus}
              style={{ backgroundColor: statusBg, color: statusColor }}
            >
              {statusText}
            </div>

            {isRejectionType ? (
              <div className={classes.rejectionReason}>
                <div>
                  <div className={classes.rejectionReasonLabel}>Rejection Reason</div>
                  <div className={classes.rejectionReasonText}>
                    {event.remarks || 'No reason provided'}
                  </div>
                </div>
              </div>
            ) : (
              <div className={classes.eventDescription}>
                <EventDescription event={event} config={config} />
              </div>
            )}
          </div>

          {canShowDetails ? (
            <div
              className={classes.viewDetailsButton}
              onClick={() => onToggle(eventId)}
            >
              <span>{isExpanded ? 'Hide Details' : 'View Details'}</span>
              {isExpanded ? (
                <UpOutlined style={{ fontSize: '10px' }} />
              ) : (
                <DownOutlined style={{ fontSize: '10px' }} />
              )}
            </div>
          ) : (
            <EventMetaInfo
              eventTime={event.event_time}
              creatorName={event.creator_name}
              classes={classes}
            />
          )}
        </div>

        {isExpanded && canShowDetails && (
          <DetailsTable
            event={event}
            config={config}
            currencySymbol={currencySymbol}
            classes={classes}
          />
        )}

        {canShowDetails && (
          <div className={classes.eventFooter}>
            <div className={classes.eventMeta}>
              {event.entity_type && event.entity_reference_number && (
                <>
                  <span className={classes.metaLabel}>
                    {transformName(event.entity_type)}
                  </span>
                  <div className={classes.metaDivider} />
                  <span className={classes.metaValue}>
                    {event.entity_reference_number}
                  </span>
                </>
              )}
            </div>

            <EventMetaInfo
              eventTime={event.event_time}
              creatorName={event.creator_name}
              classes={classes}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Events: React.FC<EventsProps> = ({
  events = [],
  classes,
  loading = false,
  currencySymbol = '',
}) => {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  const toggleEventDetails = (eventId: string) => {
    setExpandedEvents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div
        className={classes.eventsContainer}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="Loading event history..." />
      </div>
    );
  }

  return (
    <div className={classes.eventsContainer}>
      <div className={classes.eventsList}>
        <div
          className={classes.timelineLine}
          style={{
            top: '20px',
            bottom: events.length > 0 ? '56px' : '0px',
          }}
        />
        {events.map((event, index) => {
          const eventKey = event.id || `${event.event_type}_${event.event_time}_${index}`;
          return (
            <EventCard
              key={eventKey}
              event={event}
              index={index}
              isExpanded={expandedEvents.has(`${event.event_type}_${index}`)}
              onToggle={toggleEventDetails}
              currencySymbol={currencySymbol}
              classes={classes}
            />
          );
        })}
      </div>
    </div>
  );
};

const mapStateToProps = ({ masterData }) => ({
  currencySymbol: masterData?.currency_symbol || '',
});

const StyledEvents = withStyles(styles, { injectTheme: true })(Events);
export default connect(mapStateToProps)(StyledEvents);
